/**
 * Admin "Possible Duplicates" widget (Iter 150).
 *
 * Read-only review tool. Hits GET /api/admin/possible-duplicates and renders
 * pairs of listings whose normalised names look similar (default 0.85+).
 * Pure visualisation — does NOT auto-merge, hide, or delete anything.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../App';
import { Copy, RefreshCw, ExternalLink, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';

const KIND_BADGE = {
  seeded: { label: 'Seeded · Unclaimed', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  unclaimed: { label: 'Unclaimed', cls: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
  claimed: { label: 'Claimed', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  charity: { label: 'Charity / Community', cls: 'bg-sky-50 text-sky-800 border-sky-200' },
};

const KindPill = ({ kind }) => {
  const cfg = KIND_BADGE[kind] || { label: kind || '—', cls: 'bg-gray-50 text-gray-700 border-gray-200' };
  return (
    <span className={`inline-flex items-center text-[10px] font-medium uppercase tracking-wide rounded border px-1.5 py-0.5 ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

const RowCard = ({ row }) => (
  <div className="flex-1 min-w-0 border border-gray-200 rounded-lg bg-white px-3 py-2">
    <div className="flex items-start justify-between gap-2">
      <span className="font-semibold text-gray-900 text-sm truncate">{row.businessName || '—'}</span>
      <KindPill kind={row.kind} />
    </div>
    <p className="text-xs text-gray-500 mt-0.5 truncate">
      {[row.category, row.city].filter(Boolean).join(' · ') || '—'}
    </p>
    <div className="flex items-center gap-3 mt-2">
      <a
        href={`/business/${row.id}`}
        target="_blank"
        rel="noreferrer"
        className="text-xs font-medium text-purple-700 hover:text-purple-900 inline-flex items-center gap-1"
        data-testid={`dup-view-public-${row.id}`}
      >
        <ExternalLink className="h-3 w-3" /> View listing
      </a>
      <span className="text-[10px] text-gray-400">id: {(row.id || '').slice(0, 8)}…</span>
    </div>
  </div>
);

const PossibleDuplicatesWidget = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get(`/admin/possible-duplicates`, {
        params: { threshold: 0.85, limit: 50 },
      });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load duplicates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !data && !loading) load();
  }, [open, data, loading, load]);

  const count = data?.count ?? null;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 mb-8" data-testid="possible-duplicates-widget">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left"
        data-testid="possible-duplicates-toggle"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="h-5 w-5 text-gray-500" /> : <ChevronRight className="h-5 w-5 text-gray-500" />}
          <Copy className="h-5 w-5 text-amber-600" />
          <span className="font-semibold text-gray-900">Possible Duplicates</span>
          {count !== null && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${count > 0 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
              {count} pair{count === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400">Read-only review tool</span>
      </button>

      {open && (
        <div className="px-6 pb-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-gray-500 max-w-2xl">
              Pairs of listings whose names look ≥85% similar. Use this to spot legacy duplicates created
              before the signup-time check was added. <strong className="text-gray-700">No automatic action is taken.</strong>
            </p>
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-700 hover:text-purple-900 disabled:opacity-50"
              data-testid="possible-duplicates-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          {loading && !data && (
            <p className="text-sm text-gray-500 italic">Scanning listings…</p>
          )}

          {data && data.pairs.length === 0 && (
            <p className="text-sm text-emerald-700">No likely duplicates found at the current threshold.</p>
          )}

          {data && data.pairs.length > 0 && (
            <ul className="space-y-3" data-testid="possible-duplicates-list">
              {data.pairs.map((p, idx) => (
                <li
                  key={`${p.a.id}-${p.b.id}`}
                  className="border border-amber-200 bg-amber-50/40 rounded-xl p-3"
                  data-testid={`duplicate-pair-${idx}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-amber-900">
                      {Math.round(p.similarity * 100)}% match
                    </span>
                  </div>
                  <div className="flex flex-col md:flex-row gap-3">
                    <RowCard row={p.a} />
                    <RowCard row={p.b} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default PossibleDuplicatesWidget;
